import { uniq } from 'lodash'
import { differenceInSeconds } from 'date-fns'
import tracker from 'sanitas-tracking'

import { TrackerUtil } from '@/utils/Tracker'

import useCancellation from '@/hooks/useCancellation'
import useDistribution from '@/hooks/useDistribution'
import useLogger from '@/hooks/useLogger'
import usePerson from '@/hooks/usePerson'

import { AGE_RANGES, CATEGORY_TYPE, DISTRIBUTION_PARTNER_TYPE, ROUTE_NAME } from '@/config/constants'
import { EVENT_OVP } from '@/config/events'
import { FUNNELED_RULES, RULES, ANSWER } from '@/config/tracker'

// HOOKS
const { hasEmptyPreInsurer } = useCancellation()
const { getDistributionPartnerNumber } = useDistribution()
const { logError, logWarning } = useLogger()

// DATA
const COLLECTIVE_PREFIX = 'COLLECTIVE'

const PARAMS_TO_REMOVE = ['personId', 'partnerId', 'contractNumber']
const PARAMS_TO_REPLACE = { token: 'reentry' }
const PARAM_PATTERN = ':?__KEY__(\\(.*\\))?\\??'

const pendingRoutes = []

export const TrackerHelper = {
  ...TrackerUtil,
  tracker,
  COLLECTIVE_PREFIX,
  trackAction(rule, skipHistory, params) {
    if (!rule) throw new Error('Rule parameter missing')

    // FUNNELED_RULES are only triggered once
    if (Object.values(FUNNELED_RULES).includes(rule) && !!tracker.getHistory().find(item => item.event === rule)) {
      return
    }
    tracker.trigger(tracker.triggerType.ACTION, rule, skipHistory, params)
  },
  anonymizeRoute(route) {
    if (!route) {
      return
    }

    const matched = route.matched[route.matched.length - 1]
    let path = matched?.path

    const params = Object.entries(route.params).reduce((__params, [key, value]) => {
      const re = new RegExp(PARAM_PATTERN.replace('__KEY__', key))

      if (PARAMS_TO_REMOVE.includes(key)) {
        path = path.replace(re, '')
        return __params
      }

      if (Object.keys(PARAMS_TO_REPLACE).includes(key)) {
        path = path.replace(re, PARAMS_TO_REPLACE[key])
        return __params
      }

      if (!value) {
        path = path.replace(re, '')
        return __params
      }

      __params[key] = value
      path = path.replace(re, value)

      return __params
    }, {})

    path = path?.replace(/\/+/g, '/').replace(/(.*)\/$/, '$1')

    return {
      ...route,
      params,
      path,
    }
  },
  computeAgeRanges() {
    const { personsDetails } = usePerson()
    const ranges = {
      [AGE_RANGES[0]]: 0,
      [AGE_RANGES[1]]: 0,
      [AGE_RANGES[2]]: 0,
      [AGE_RANGES[3]]: 0,
      [AGE_RANGES[4]]: 0,
    }

    Object.keys(personsDetails.value).forEach(personId => {
      const __age = personsDetails.value[personId].age

      if (__age < 0) ranges[AGE_RANGES[0]]++
      else if (__age <= 18) ranges[AGE_RANGES[1]]++
      else if (__age <= 26) ranges[AGE_RANGES[2]]++
      else if (__age <= 44) ranges[AGE_RANGES[3]]++
      else if (__age <= 63) ranges[AGE_RANGES[4]]++
      else ranges[AGE_RANGES[5]]++
    })

    return ranges
  },
  computeNumberOfUploads(persons) {
    return persons.reduce((acc, curr) => {
      return acc + curr?.preInsurer?.residencePermitFiles?.length
    }, 0)
  },
  computePreInsurerVVG(persons, grouped = false) {
    const insurer = persons.flatMap(person => {
      if (hasEmptyPreInsurer(person.preInsurerVvgs)) {
        return []
      }

      return person.preInsurerVvgs.map(__insurer => {
        return __insurer.preInsurerId
      })
    })

    if (grouped) {
      return uniq(insurer).sort()
    } else {
      return insurer.sort()
    }
  },
  computeProductsCombination(persons, productsStructure) {
    if (!productsStructure) {
      return
    }

    const getPersonCategories = person => {
      const personProducts = TrackerUtil.getPersonProducts(person)
      return personProducts.reduce((categories, product) => {
        categories.push(TrackerUtil.getProductCategory(productsStructure, product.productId))
        return categories
      }, [])
    }

    const allCategories = persons
      .reduce((__allCategories, person) => {
        for (const category of getPersonCategories(person)) {
          if (!__allCategories.includes(category)) {
            __allCategories.push(category)
          }
        }
        return __allCategories
      }, [])
      .filter(category => category)

    if (!allCategories.length) {
      return ANSWER.NONE
    } else if (allCategories.length === 1) {
      return allCategories.includes(CATEGORY_TYPE.KVG) ? 'KVGOnly' : 'VVGOnly'
    } else {
      return 'KVG&VVG'
    }
  },
  computeSecondsTillNow(createDate) {
    const now = new Date()
    const then = new Date(createDate)
    return differenceInSeconds(now, then)
  },
  setFormTrackingData(event, payload) {
    const data = tracker.get(`forms.ovp.${payload.form}`) ?? []

    data.push({
      name: payload.form,
      event,
      errors: payload.errors,
      fieldName: payload?.fieldName,
    })

    tracker.set({
      data: {
        forms: {
          ovp: {
            [`${payload.form}`]: data,
          },
        },
      },
    })
  },
  setTrackingSourceComponent(sourceComponent) {
    if (!sourceComponent) {
      logError(['sourceComponent falsy', sourceComponent])
      return
    }
    tracker.set({ data: { sourceComponent } })
  },
  setDistributionPartners(distributionPartners) {
    if (!distributionPartners || !Array.isArray(distributionPartners)) {
      logWarning(['distributionPartners not set', distributionPartners])
      return
    }

    // special case 'ABSCHLUSS' can have two entries
    const abschluss = distributionPartners
      .filter(partner => partner.distributionPartnerType === DISTRIBUTION_PARTNER_TYPE.ABSCHLUSS)
      .map(partner => partner.distributionPartnerNumber)
      .sort()
      .join(';')

    const trackingDistributionPartners = {
      offerte: getDistributionPartnerNumber(distributionPartners, DISTRIBUTION_PARTNER_TYPE.OFFERTE),
      betreuer: getDistributionPartnerNumber(distributionPartners, DISTRIBUTION_PARTNER_TYPE.BETREUER),
      abschluss,
    }

    tracker.set({
      data: { distributionPartners: trackingDistributionPartners },
    })
  },

  /**
   *  <string>source
   *  <boolean>strict
   */
  trackEntering(opts = { source: undefined, strict: false }) {
    const key = 'sanitas-dvp-ovp_entered-source'
    if (opts.source) {
      // write to sessionStorage
      sessionStorage.setItem(key, opts.source)
    } else {
      const _source = sessionStorage.getItem(key)
      if (_source) {
        tracker.set({
          data: {
            personsScreenSource: _source,
          },
        })
        sessionStorage.removeItem(key)
      }

      // if strict is not truthy, then a source has to be set!
      if ((opts.strict && _source) || !opts.strict) {
        TrackerHelper.trackAction(RULES.OVP_ENTERED)
      }
    }
  },

  /**
   * prepare data for action 'create default offer' with source of trigger
   */
  trackDefaultOfferSource(source = undefined) {
    const key = 'sanitas-dvp-default_offer_source'

    if (source) {
      sessionStorage.setItem(key, source)
    } else {
      const _source = sessionStorage.getItem(key)
      if (_source) {
        tracker.set({
          data: {
            defaultOfferSource: _source,
          },
        })
        sessionStorage.removeItem(key)
      }

      TrackerHelper.trackAction(EVENT_OVP.DEFAULT_BASKET_CREATED)
    }
  },

  trackInteraction(route) {
    let params = route?.params

    // Exception for additional...
    if (route.name === 'additional' || route.name === 'groups-collective') {
      params.categoryId = 'ADDITIONAL'
    }

    if (params?.categoryId) {
      const type = RULES[params.groupId || params.categoryId]
      if (type) {
        TrackerHelper.trackAction(type)
      } else if (!route?.meta?.aide) {
        logError(`RULE ${params.groupId || params.categoryId} NOT FOUND`)
      }
    }
  },
  trackRoute(to, from) {
    const isLanguageSet = tracker.get('page.language')

    if (!isLanguageSet) {
      tracker.set({
        data: { page: { language: document.documentElement.lang } },
      })
    }

    if (tracker.get('basket.id')) {
      this.triggerPageView(to, from)
    } else if (to?.name !== 'reentry') {
      pendingRoutes.push(to)
    }
  },
  triggerPageView(to, from) {
    const route = to && this.anonymizeRoute(to)
    const routeFrom = from && this.anonymizeRoute(from)

    const level = route.path.split('/').length - 1
    const shouldTriggerPageView =
      route?.path !== routeFrom?.path &&
      (level === 1 ||
        route.name.startsWith('aide-') ||
        route.name.startsWith('application-') ||
        route.name.startsWith('digital-signature') ||
        route.name.includes('basket') ||
        !tracker.getHistory().find(item => item.event === RULES.OVP_PAGE_VIEWED))

    if (shouldTriggerPageView) {
      const language = tracker.get('page.language')
      const host = window.location.protocol + '//www.sanitas.com'
      const baseAliasUrl = host + '/de/privatkunden/praemien.html'
      const baseUrl = host + window.location.pathname.replace(/(.*)\/$/, '$1')
      const url = baseUrl + route.path
      const aliasUrl = baseAliasUrl + route.path
      const pageName = route.name
      const pageTitle = pageName ? pageName[0].toUpperCase() + pageName.slice(1) : null

      const data = {
        page: {
          url,
          aliasUrl,
          route: pageTitle,
          containingPage: pageName,
          title: pageTitle,
          description: `Ovp - ${language.toUpperCase()} - ${pageTitle}`,
          descriptionWithoutLang: `Ovp - ${pageTitle}`,
        },
      }

      tracker.set({ data })
      tracker.trigger(tracker.triggerType.VIEW, RULES.OVP_PAGE_VIEWED)

      triggerRouteBasedActions(to, from)
    }
    this.trackInteraction(route)
  },
}

/**
 * Triggers funnel rules and other route based actions
 * @param to
 * @param from
 */
function triggerRouteBasedActions(to, from) {
  if (to?.name) {
    switch (to.name) {
      case ROUTE_NAME.BASKET:
      case ROUTE_NAME.AIDE.BASKET:
        TrackerHelper.trackAction(RULES.OVP_SCREEN_BASKET_FUNNELED)

        if (from?.name === 'reentry' || from?.name === 'reentry.offer-application') {
          TrackerHelper.trackAction(RULES.OVP_DAP_RE_ENTERED)
        }
        break

      case ROUTE_NAME.ONBOARDING:
        TrackerHelper.trackAction(RULES.OVP_SCREEN_DAP_ONBOARDING_FUNNELED)
        TrackerHelper.trackAction(RULES.OVP_DAP_ENTERED)
        break

      case ROUTE_NAME.APPLICATION_EXISTING_CUSTOMER_DATA:
        TrackerHelper.trackAction(RULES.OVP_SCREEN_DAP_EXISTING_CUSTOMER_FUNNELED)
        break

      case ROUTE_NAME.APPLICATION_PERSONAL_DATA:
        TrackerHelper.trackAction(RULES.OVP_SCREEN_DAP_PERSONS_FUNNELED)
        break

      case ROUTE_NAME.APPLICATION_ADMIN_DATA:
        TrackerHelper.trackAction(RULES.OVP_SCREEN_DAP_ADMIN_FUNNELED)
        break

      case ROUTE_NAME.APPLICATION_HEALTHQUESTIONS:
        TrackerHelper.trackAction(RULES.OVP_SCREEN_DAP_HEALTH_QUESTIONS_FUNNELED)
        break

      case ROUTE_NAME.SIGNATURE_ENTRY:
        TrackerHelper.trackAction(RULES.OVP_SCREEN_SIGNATURE_FUNNELED)
        break

      case ROUTE_NAME.SIGNATURE_VERIFICATION:
        TrackerHelper.trackAction(RULES.OVP_SCREEN_SIGNATURE_VERIFICATION_FUNNELED)
        break

      case ROUTE_NAME.SUBMIT:
        TrackerHelper.trackAction(RULES.OVP_SCREEN_DAP_SUMMARY_FUNNELED)
        break
    }
  }
}

if (import.meta.env.VITE_TRACKER_LOGGING) {
  window.Sanitas.Tracking.loggable = true
}

if (import.meta.env.VITE_TRACKER_ENABLED) {
  window.Sanitas.Tracking.enabled = true
}

window.addEventListener('TRACKER_DATALAYER_UPDATED', () => {
  if (!pendingRoutes.length || !tracker.get('basket.id')) {
    return
  }

  if (pendingRoutes.length > 1) {
    logWarning(['Tracker: more than one view pending. Triggering the most recent.', [...pendingRoutes]])
  }

  const route = pendingRoutes.pop()
  TrackerHelper.triggerPageView(route)
})
